/**
 * `parseEvaluation` — markdown → structured sections (Batch 30j-3).
 *
 * Pure function. Parses an LLM-emitted markdown evaluation into the
 * structured shape consumed by `<EvaluationCards>` (Recommendation +
 * Detailed Assessment split panel).
 *
 * Spec: `frontend/docs/30j_3_design.md` §4 + §5 + `frontend/docs/30j_3_architect.md` §6.
 *
 * Port of prototype `parseEvalSections` + `extractVerdict` + `verdictColor`
 * + `SECTION_COLORS` + `shortenLabel` (`~/certain-prototype/public/pipeline.html:557-769`),
 * with the two HoD-noted foundations alignments (gaps amber-not-red,
 * tenure orange-not-amber) and two-channel verdict normalization (raw
 * drives color; normalized drives `<VerdictBadge>` label).
 *
 * Defensive guards:
 *   - Strips `## Scorecard` blocks defensively (they belong to 30j-5,
 *     not 30j-3; defensive in case of prompt drift).
 *   - Heading collisions get a `(cont.)` suffix so a duplicate `## Strengths`
 *     does not silently overwrite the first (QA edge case E11).
 *   - No `## H2` headings → fallback path returns `fallbackBody` populated
 *     with the raw analysis (rendered as a single full-width markdown
 *     block by the caller).
 *
 * Pure: no React imports, no hooks. Caller memoizes.
 */
import type { Verdict } from "@/components/candidates/VerdictBadge";

// ── Public types ──────────────────────────────────────────────────────────────

interface SplitPanelNavItem {
  /** Stable identifier (matches `activeKey`). */
  key: string;
  /** Visible label (shortened per `shortenLabel`). */
  label: string;
  /** Optional leading icon — node OR string (emoji / numeric badge). */
  icon?: string;
  /** Active-state accent color (CSS color string). */
  color?: string;
}

export interface ParsedEvaluation {
  /** Map of raw section heading → section body markdown. */
  sections: Record<string, string>;
  /** Map of nav key → raw section heading (for detail label). */
  rawKeyByKey: Record<string, string>;
  /** Map of nav key → resolved CSS variable color (for detail label color). */
  colorByKey: Record<string, string>;
  /** Ordered nav items for `<SplitPanel>`. Excludes the recommendation. */
  navItems: SplitPanelNavItem[];
  /** Body of the Recommendation section, hoisted to the top card. Null if absent. */
  recommendationBody: string | null;
  /** Raw verdict string from prototype VERDICTS list (drives color). */
  verdictRaw: string | null;
  /** CSS variable string for verdict color (drives `<CollapsibleCard borderColor>`). */
  verdictColor: string;
  /** Normalized 4-bucket verdict for `<VerdictBadge>`. Null if no verdict found. */
  verdictNormalized: Verdict | null;
  /** Fallback rendering body — set when no `## H2` sections exist at all. */
  fallbackBody: string | null;
}

// ── Verdict canon ─────────────────────────────────────────────────────────────

/**
 * Verdict phrases the prompt may emit. ORDER MATTERS — longer phrases
 * checked first so "Strong Proceed" is not misread as "Proceed".
 */
const VERDICTS = [
  "Strong Hire",
  "Strong Pass",
  "Strong Proceed",
  "Proceed with Caution",
  "Hire",
  "Pass",
  "Proceed",
] as const;

/** Find the first verdict phrase present in the recommendation body. */
export function extractVerdict(body: string): string | null {
  for (const v of VERDICTS) {
    if (body.includes(v)) return v;
  }
  return null;
}

/**
 * Map raw verdict to a CSS variable color. Foundations §1.3 verdict tokens.
 * Returns the section-recommendation token when verdict is null so the
 * Recommendation card border tint always resolves.
 */
export function verdictColor(v: string | null): string {
  if (!v) return "var(--section-recommendation)";
  if (v === "Strong Hire" || v === "Strong Proceed") {
    return "var(--verdict-strong-fg)";
  }
  if (v === "Hire" || v === "Proceed") return "var(--verdict-moderate-fg)";
  if (v === "Proceed with Caution") return "var(--verdict-watch-fg)";
  // "Pass" or "Strong Pass"
  return "var(--verdict-weak-fg)";
}

/**
 * Normalize raw verdict to the 4-bucket `<VerdictBadge>` set. The badge's
 * label set is FROZEN; the prompt may emit 7 phrases. Two-channel posture:
 * raw drives COLOR (so "Strong Hire" tints emerald), normalized drives
 * BADGE LABEL (so the badge reads "Strong Proceed").
 */
export function normalizeVerdict(raw: string | null): Verdict | null {
  if (!raw) return null;
  if (raw === "Strong Hire" || raw === "Strong Proceed") return "Strong Proceed";
  if (raw === "Hire" || raw === "Proceed") return "Proceed";
  if (raw === "Proceed with Caution") return "Proceed with Caution";
  // "Pass" / "Strong Pass"
  return "Pass";
}

// ── Section colors ────────────────────────────────────────────────────────────

/**
 * Pattern → CSS token. First match wins. Case-insensitive substring match
 * on the raw section key, mirroring prototype semantics.
 *
 * Foundations §1.3 alignments vs prototype:
 *   - `gaps`/`probe` → amber (`--section-gaps`), NOT red. Foundations
 *     reserves red for verdict failure / destructive actions; gaps is
 *     investigative attention, not failure.
 *   - `tenure`/`stability` → orange (`--section-tenure`), NOT amber.
 *     Differentiates tenure axis from amber gaps axis (prevents two
 *     adjacent amber items in nav).
 */
const SECTION_COLOR_RULES: ReadonlyArray<{ pattern: RegExp; token: string }> = [
  { pattern: /strength/i, token: "var(--section-strengths)" },
  { pattern: /gap|probe/i, token: "var(--section-gaps)" },
  { pattern: /technical|tech depth/i, token: "var(--section-technical)" },
  { pattern: /tenure|stability/i, token: "var(--section-tenure)" },
  { pattern: /culture|cultural|leadership/i, token: "var(--section-culture)" },
  {
    pattern: /recommendation|bottom line/i,
    token: "var(--section-recommendation)",
  },
  { pattern: /overview|overall assessment/i, token: "var(--section-overview)" },
];
const SECTION_COLOR_DEFAULT = "var(--section-overview)";

export function sectionColor(rawKey: string): string {
  for (const { pattern, token } of SECTION_COLOR_RULES) {
    if (pattern.test(rawKey)) return token;
  }
  return SECTION_COLOR_DEFAULT;
}

// ── Label shortening ──────────────────────────────────────────────────────────

/**
 * Hard-coded abbreviation map for the canonical eval section titles.
 * Both common spellings of "Technical/Domain Depth Assessment" map to
 * the same short label.
 */
const ABBREV: Record<string, string> = {
  "Candidate Overview": "Overview",
  "Key Strengths": "Strengths",
  "Gaps & Open Questions": "Open Questions",
  "Technical / Domain Depth Assessment": "Tech Depth",
  "Technical/Domain Depth Assessment": "Tech Depth",
  "Tenure & Stability Assessment": "Tenure & Stability",
  "Cultural Fit Assessment": "Cultural Fit",
  "Pre-Interview Recommendation": "Recommendation",
  "Bottom Line": "Bottom Line",
};

/** Shorten a section heading for the SplitPanel nav. 22-char truncation cap. */
export function shortenLabel(rawKey: string): string {
  // Numbered: "1. The Demo: A Strong First Impression" → "The Demo" or first 4 words.
  const numMatch = rawKey.match(/^(\d+)\.\s*(.+)/);
  if (numMatch) {
    const body = numMatch[2] ?? "";
    const colonIdx = body.indexOf(":");
    const short =
      colonIdx > 0
        ? body.slice(0, colonIdx)
        : body.split(" ").slice(0, 4).join(" ");
    return short.length > 22 ? short.slice(0, 22) + "…" : short;
  }
  const mapped = ABBREV[rawKey];
  if (mapped) return mapped;
  return rawKey.length > 22 ? rawKey.slice(0, 22) + "…" : rawKey;
}

// ── Slug helper ───────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

// ── Empty result ──────────────────────────────────────────────────────────────

function emptyResult(fallbackBody: string | null): ParsedEvaluation {
  return {
    sections: {},
    rawKeyByKey: {},
    colorByKey: {},
    navItems: [],
    recommendationBody: null,
    verdictRaw: null,
    verdictColor: "var(--section-recommendation)",
    verdictNormalized: null,
    fallbackBody,
  };
}

// ── Main entry ────────────────────────────────────────────────────────────────

/**
 * Parse a markdown AI evaluation into structured sections.
 *
 * Flow:
 *   1. Strip `## Scorecard` blocks defensively.
 *   2. Split on `## H2` headings.
 *   3. Hoist Recommendation section (key matches /recommendation|bottom line/i).
 *   4. Build nav items for the remaining drill-down sections.
 *   5. Extract + normalize verdict from the recommendation body.
 *
 * Returns a structured result. If no `## H2` sections were found, returns
 * a fallback shape with `fallbackBody = stripped` so the caller can render
 * the analysis as a single block.
 */
export function parseEvaluation(rawAnalysis: string): ParsedEvaluation {
  if (typeof rawAnalysis !== "string" || rawAnalysis.trim().length === 0) {
    return emptyResult(null);
  }

  // Step 1 — strip scorecard block (belongs to 30j-5; defensive insurance).
  const stripped = rawAnalysis
    .replace(/## Scorecard\n[\s\S]*?(?=\n## |\n###|$)/, "")
    .trim();

  if (stripped.length === 0) return emptyResult(null);

  // Step 2 — split into H2 sections. Disambiguate duplicates with a (cont.)
  // suffix so a future prompt regression that emits two `## Strengths`
  // blocks doesn't silently overwrite.
  const parts = stripped.split(/\n(?=## )/);
  const sections: Record<string, string> = {};
  for (const part of parts) {
    const m = part.match(/^## (.+?)\n([\s\S]*)/);
    if (!m) continue;
    const rawHeading = m[1] ?? "";
    const rawBody = m[2] ?? "";
    const baseHeading = rawHeading.trim();
    const body = rawBody.trim();
    let heading = baseHeading;
    let suffix = 1;
    while (Object.prototype.hasOwnProperty.call(sections, heading)) {
      // Render `(cont.)` for the first dup, `(cont. 2)` for the second, etc.
      heading =
        suffix === 1
          ? `${baseHeading} (cont.)`
          : `${baseHeading} (cont. ${suffix})`;
      suffix += 1;
    }
    sections[heading] = body;
  }

  // No H2 sections at all → fallback to single-block rendering.
  if (Object.keys(sections).length === 0) {
    return emptyResult(stripped);
  }

  // Step 3 — recommendation hoisting (case-insensitive substring match).
  const allKeys = Object.keys(sections);
  const recKey =
    allKeys.find((k) => /recommendation|bottom line/i.test(k)) ?? null;
  const recommendationBody = recKey ? (sections[recKey] ?? null) : null;

  // Step 4 — build drill-down nav items (every section except the rec).
  const drillKeys = allKeys.filter((k) => k !== recKey);
  const rawKeyByKey: Record<string, string> = {};
  const colorByKey: Record<string, string> = {};
  const navItems: SplitPanelNavItem[] = drillKeys.map((rawKey, i) => {
    const numMatch = rawKey.match(/^(\d+)\./);
    const icon = numMatch ? numMatch[1] : "◆";
    const navKey = `s${i}-${slugify(rawKey)}`;
    const color = sectionColor(rawKey);
    rawKeyByKey[navKey] = rawKey;
    colorByKey[navKey] = color;
    return {
      key: navKey,
      label: shortenLabel(rawKey),
      icon,
      color,
    };
  });

  // Step 5 — verdict extraction + normalization (two-channel).
  const verdictRaw = recommendationBody ? extractVerdict(recommendationBody) : null;
  const resolvedVerdictColor = verdictColor(verdictRaw);
  const verdictNormalized = normalizeVerdict(verdictRaw);

  return {
    sections,
    rawKeyByKey,
    colorByKey,
    navItems,
    recommendationBody,
    verdictRaw,
    verdictColor: resolvedVerdictColor,
    verdictNormalized,
    fallbackBody: null,
  };
}
